import { useEffect, useRef, useState, useCallback } from 'react';
import { toNormalized } from '../helpers/coords';

export function useFreehandDrawing(
    pdfLayersRef: React.RefObject<HTMLDivElement | null>,
    setPendingComment: React.Dispatch<React.SetStateAction<string>>,
    setPendingPolygon: React.Dispatch<React.SetStateAction<Array<{ x: number; y: number }> | null>>,
) {
    const [freehandActive, setFreehandActive] = useState(false);
    const freehandDrawingRef = useRef(false);
    const freehandStartRef = useRef<{ x: number; y: number } | null>(null);
    const freehandPointsRef = useRef<Array<{ x: number; y: number }>>([]);
    const freehandPolylineRef = useRef<SVGPolylineElement | null>(null);
    const freehandStartCircleRef = useRef<SVGCircleElement | null>(null);

    const resetFreehand = useCallback(() => {
        freehandDrawingRef.current = false;
        freehandStartRef.current = null;
        freehandPointsRef.current = [];
        freehandPolylineRef.current?.setAttribute('points', '');
        freehandStartCircleRef.current?.setAttribute('visibility', 'hidden');
        setFreehandActive(false);
    }, []);

    const finalizeFreehand = useCallback(() => {
        const pts = freehandPointsRef.current;
        if (pts.length < 3) { resetFreehand(); return; }
        freehandDrawingRef.current = false;
        freehandPolylineRef.current?.setAttribute('points', '');
        freehandStartCircleRef.current?.setAttribute('visibility', 'hidden');
        freehandPointsRef.current = [];
        freehandStartRef.current = null;
        setFreehandActive(false);
        setPendingPolygon(pts);
        setPendingComment('');
    }, [resetFreehand, setPendingPolygon, setPendingComment]);

    useEffect(() => {
        const el = pdfLayersRef.current;
        if (!el) return;
        const CLOSE_THRESHOLD = 14;
        const MIN_DIST = 3;

        const onMove = (e: MouseEvent) => {
            if (!freehandDrawingRef.current) return;
            const base = el.getBoundingClientRect();
            const dim = { w: el.offsetWidth, h: el.offsetHeight };
            const { nx, ny, px, py } = toNormalized(e.clientX, e.clientY, base, dim.w, dim.h);

            const pts = freehandPointsRef.current;
            const last = pts[pts.length - 1];
            const lx = last.x * dim.w;
            const ly = last.y * dim.h;
            if (Math.hypot(px - lx, py - ly) < MIN_DIST) return;

            pts.push({ x: nx, y: ny });

            freehandPolylineRef.current?.setAttribute(
                'points',
                pts.map((p) => `${p.x * dim.w},${p.y * dim.h}`).join(' ')
            );

            const start = freehandStartRef.current!;
            const spx = start.x * dim.w;
            const spy = start.y * dim.h;
            if (pts.length > 10 && Math.hypot(px - spx, py - spy) < CLOSE_THRESHOLD) {
                finalizeFreehand();
            }
        };

        el.addEventListener('mousemove', onMove);
        return () => el.removeEventListener('mousemove', onMove);
    }, [finalizeFreehand, pdfLayersRef]);

    const startFreehand = useCallback((e: React.MouseEvent) => {
        if (freehandDrawingRef.current) return;
        const layers = pdfLayersRef.current;
        if (!layers) return;
        const dim = { w: layers.offsetWidth, h: layers.offsetHeight };
        if (!dim.w || !dim.h) return;
        const base = layers.getBoundingClientRect();
        const { nx, ny, px, py } = toNormalized(e.clientX, e.clientY, base, dim.w, dim.h);
        freehandDrawingRef.current = true;
        freehandStartRef.current = { x: nx, y: ny };
        freehandPointsRef.current = [{ x: nx, y: ny }];
        setFreehandActive(true);
        if (freehandStartCircleRef.current) {
            freehandStartCircleRef.current.setAttribute('cx', String(px));
            freehandStartCircleRef.current.setAttribute('cy', String(py));
            freehandStartCircleRef.current.setAttribute('visibility', 'visible');
        }
    }, [pdfLayersRef]);

    return {
        freehandActive,
        freehandPolylineRef,
        freehandStartCircleRef,
        freehandDrawingRef,
        resetFreehand,
        startFreehand,
    };
}
